import { atom } from 'jotai'
import { MutableRefObject, useEffect } from 'react'

import { getStore } from '@/store'

import {
	contentFormValidityAtom,
	isContentTouchedAtom,
} from '../../generatorState'
import { GENERATOR_SEARCH_PARAM, QR_TYPE } from '../../generatorTypes'
import { useGeneratorSearchParam } from '../../hooks'
import { getGeneratorSearchParam } from '../../utils/generatorSearchParam'
import { QR_TYPE_TO_CONTENT_ATOM } from '../../utils/qrTypeToContentAtom'
import { createQr, isContentFormSubmittingAtom } from '../contentFormUtils'

// Recomputes on either the per-section validity map or the touched flag
// changing, so a real edit that doesn't happen to flip a section's isValid
// value (e.g. a required field whose pre-validation isValid default is
// already true) still re-triggers the check below.
const qrCreationReadinessAtom = atom((get) => ({
	sectionsValidity: get(contentFormValidityAtom),
	isContentTouched: get(isContentTouchedAtom),
}))

export const useCreateQrOnFirstValid = (
	createQrOnFirstValidAbortSignalRef: MutableRefObject<AbortController | null>,
	qrType: QR_TYPE,
) => {
	const qrId = useGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)

	// Handle initial creation when form first becomes valid
	useEffect(() => {
		if (qrId) {
			return
		}

		const unsubscribeValidity = getStore().sub(
			qrCreationReadinessAtom,
			async () => {
				const { sectionsValidity, isContentTouched } = getStore().get(
					qrCreationReadinessAtom,
				)
				const isFormValid = Object.values(sectionsValidity).every(
					(isValid) => isValid,
				)
				const isQrNotSaved = !getGeneratorSearchParam(
					GENERATOR_SEARCH_PARAM.Id,
				)
				const isSubmitting = getStore().get(isContentFormSubmittingAtom)

				if (
					isQrNotSaved &&
					isFormValid &&
					isContentTouched &&
					!isSubmitting
				) {
					const content = getStore().get(
						QR_TYPE_TO_CONTENT_ATOM[qrType],
					)
					const controller = new AbortController()
					createQrOnFirstValidAbortSignalRef.current = controller
					unsubscribeValidity()
					await createQr(qrType, content, controller.signal)
				}
			},
		)

		return unsubscribeValidity
	}, [createQrOnFirstValidAbortSignalRef, qrId, qrType])
}
